import { useEffect, useState } from "react";
import { usePageState } from "../../Layout";
import { apiUrl, executeRequest, getToken } from "../../functions/requests";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import IconLock from "../../icons/IconLock";
import Spacer from "../../components/Spacer";
import Button from "../../components/Button";
import FormInput from "../../components/FormInput";
import SubscriptionPlan from "../../components/SubscriptionPlan";
import PaymentForm from "../../components/PaymentForm";

export default function Checkout() {
  const { pageState, setPageState } = usePageState();
  const [userData, setUserData] = useState(
    pageState ? pageState.userData : null
  );

  // Loading
  const [loading, setLoading] = useState(true);

  // Load your Stripe publishable key
  const stripePromise = loadStripe(
    "pk_test_51QEfAqAIsfXMop9N5hsSsOVceuyXgaPtPeHyO8q9mfD6Ag09hCeSxlLqjMjIMW21ubZhQtaM1MlvoZvRWZbWs3wk00JTHySeJa"
  );

  // Dropdown data
  const [applications, setApplications] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);

  // Selected data
  const [selectedApplicationUuid, setSelectedApplicationUuid] = useState(null);
  const [
    selectedSubscriptionPlanDuration,
    setSelectedSubscriptionPlanDuration,
  ] = useState(null);
  const [selectedSubscriptionPlanSlug, setSelectedSubscriptionPlanSlug] =
    useState(null);
  const [selectedStoresCount, setSelectedStoresCount] = useState(1);
  const [renewAutomatically, setRenewAutomatically] = useState(true);

  // Show payment page final step
  const [showCompletePaymentPage, setShowCompletePaymentPage] = useState(false);
  const [selectedSubscriptionPlan, setSelectedSubscriptionPlan] =
    useState(null);

  // Popup with payment URL
  const [paymentPageUrl, setPaymentPageUrl] = useState(null);

  // Get data to fill select fields
  const getDropdownData = async () => {
    setLoading(true);
    await executeRequest(
      apiUrl + "customer-subscriptions/dropdown-data/" + userData.uuid,
      "GET",
      null,
      {
        Authorization: "Bearer " + getToken(),
      },
      (data) => {
        console.log(data);
        setApplications(data.applications);
        setSubscriptionPlans(data.subscription_plans);
      },
      (error) => {
        console.log(error);
      }
    );
    setLoading(false);
  };

  // Function to show payment page
  const showPaymentPage = async () => {
    if (
      !userData ||
      userData.user_type !== "customer" ||
      !selectedSubscriptionPlanDuration ||
      !selectedSubscriptionPlanSlug
    )
      return;

    // Get selected plan
    setSelectedSubscriptionPlan(
      subscriptionPlans[selectedApplicationUuid][
        selectedSubscriptionPlanDuration
      ].find((plan) => plan.slug === selectedSubscriptionPlanSlug)
    );
    setShowCompletePaymentPage(true);
  };

  const completePayment = async (paymentId) => {
    // Create a new FormData object
    const formData = new FormData();

    // Append the necessary fields to the formData object
    formData.append("customer_uuid", userData.uuid);
    formData.append("subscription_plan_uuid", selectedSubscriptionPlan.uuid);
    formData.append("stripe_payment_method_id", paymentId);
    formData.append("stores_count", selectedStoresCount);
    formData.append("renew_automatically", renewAutomatically);

    console.log(userData.uuid);
    console.log(selectedSubscriptionPlan.uuid);
    console.log(paymentId);
    console.log(selectedStoresCount);
    console.log(renewAutomatically);

    // Execute the request
    await executeRequest(
      `${apiUrl}customer-subscriptions/pay`,
      "POST",
      formData,
      {
        Authorization: "Bearer " + getToken(),
      },
      (data) => {
        console.log(data);
      },
      (error) => {
        console.log(error);
      }
    );
    setLoading(false);
  };

  // Run initial actions
  useEffect(() => {
    getDropdownData();
  }, []);

  // Render HTML
  return (
    <div>
      <h1 className="page-title">Checkout</h1>
      {!showCompletePaymentPage ? (
        <>
          <h2>
            Select an application to view the available subscription plans:
          </h2>
          <Spacer height="10px" />
          <div className="flex flex-gap-s">
            {applications.map((application, index) => {
              return (
                <Button
                  key={index}
                  text={application.name}
                  onClicked={() => setSelectedApplicationUuid(application.uuid)}
                  disabled={application.uuid === selectedApplicationUuid}
                  active={application.uuid === selectedApplicationUuid}
                />
              );
            })}
          </div>
          <Spacer height="var(--spaceXL)" />
          {selectedApplicationUuid &&
            subscriptionPlans[selectedApplicationUuid] && (
              <>
                <h2>Plans</h2>
                <Spacer height="10px" />
                <div className="flex flex-gap-m">
                  {Object.keys(subscriptionPlans[selectedApplicationUuid]).map(
                    (durationKey, index) => {
                      // Use the durationKey to determine the button label
                      const buttonLabel =
                        durationKey === "months-1"
                          ? "Monthly"
                          : durationKey === "months-6"
                          ? "6 Months"
                          : "Annual";

                      return (
                        <Button
                          key={index} // Use a unique key for each button
                          text={buttonLabel}
                          loading={loading}
                          disabled={
                            loading ||
                            selectedSubscriptionPlanDuration === durationKey
                          }
                          active={
                            selectedSubscriptionPlanDuration === durationKey
                          }
                          onClicked={() =>
                            setSelectedSubscriptionPlanDuration(durationKey)
                          }
                        />
                      );
                    }
                  )}
                </div>
              </>
            )}
          <Spacer height="var(--spaceXL)" />
          <div className="flex flex-gap-m">
            {selectedApplicationUuid &&
              subscriptionPlans &&
              selectedSubscriptionPlanDuration &&
              subscriptionPlans[selectedApplicationUuid][
                selectedSubscriptionPlanDuration
              ].length > 0 &&
              subscriptionPlans[selectedApplicationUuid][
                selectedSubscriptionPlanDuration
              ].map((subscriptionPlan, index) => {
                return (
                  <SubscriptionPlan
                    key={index}
                    name={subscriptionPlan.name}
                    price={subscriptionPlan.cost + " €"}
                    priceDescription="per store"
                    paymentText={
                      selectedSubscriptionPlanSlug === subscriptionPlan.slug
                        ? "Unselect"
                        : "Select"
                    }
                    description={subscriptionPlan.description}
                    paymentAction={() =>
                      setSelectedSubscriptionPlanSlug(
                        selectedSubscriptionPlanSlug === subscriptionPlan.slug
                          ? null
                          : subscriptionPlan.slug
                      )
                    }
                    selected={
                      subscriptionPlan.slug === selectedSubscriptionPlanSlug
                    }
                  />
                );
              })}
          </div>
          {selectedSubscriptionPlanSlug && (
            <>
              <Spacer />
              <h2>How many stores?</h2>
              <div>You can always add more stores as you go.</div>
              <Spacer height="10px" />
              <FormInput
                type="number"
                min="1"
                max="999999"
                step="1"
                value={selectedStoresCount}
                onChanged={(value) => setSelectedStoresCount(value)}
              />
              <Spacer />
              <h2>
                Total:{" "}
                {selectedStoresCount *
                  subscriptionPlans[selectedApplicationUuid][
                    selectedSubscriptionPlanDuration
                  ].find((plan) => plan.slug === selectedSubscriptionPlanSlug)
                    .cost}{" "}
                €
              </h2>
              <Spacer />
              <h2>Renew Automatically?</h2>
              <div>
                Enabling automatic renewal ensures your subscription is charged
                7 days before it expires, preventing any disruption to your
                service.
              </div>
              <div>
                If payment fails, you will need to manually complete the payment
                to continue using the package.
              </div>
              <div>
                To retain access to all your data, you must renew with the same
                storage plan as before.
              </div>
              <Spacer height="10px" />
              <FormInput
                type="select"
                selectAllowEmptyOption={false}
                selectOptions={[
                  { name: "Yes", uuid: "yes" },
                  { name: "No", uuid: "no" },
                ]}
                value={renewAutomatically}
                onChanged={(value) =>
                  setRenewAutomatically(value === true ? "yes" : "no")
                }
                showTitle={false}
              />
            </>
          )}
          <Spacer />
          <Button
            text="Secure Online Payment"
            icon={<IconLock />}
            onClicked={() => showPaymentPage()}
            loading={loading}
            disabled={
              loading ||
              !selectedSubscriptionPlanDuration ||
              !selectedSubscriptionPlanSlug ||
              selectedStoresCount < 1
            }
          />
        </>
      ) : (
        <>
          <Button
            type="underlined"
            text="< Back to subscription plans"
            onClicked={() => {
              setShowCompletePaymentPage(false);
              setSelectedSubscriptionPlan(null);
            }}
          />
          <Spacer />
          <h2>
            {selectedSubscriptionPlan.application.name}{" "}
            {selectedSubscriptionPlan.name}
          </h2>
          <Spacer height="10px" />
          <h3>
            {selectedSubscriptionPlan.cost * selectedStoresCount}€ (
            {selectedStoresCount} stores of {selectedSubscriptionPlan.cost}€
            each)
          </h3>
          <Spacer />
          <div style={{ width: "100%", maxWidth: "500px" }}>
            <Elements stripe={stripePromise}>
              <PaymentForm
                onCompletePayment={(paymentId) => completePayment(paymentId)}
              />
            </Elements>
          </div>
        </>
      )}
    </div>
  );
}
